import React, { useState, useEffect, useRef } from 'react';
import { Search, ChevronDown, Check } from 'lucide-react';

interface Stock {
    symbole: string;
    nom_societe: string;
}

interface StockSearchProps {
    stocks: Stock[];
    value: string;
    onChange: (symbole: string) => void;
    placeholder?: string;
    disabled?: boolean;
}

const StockSearch: React.FC<StockSearchProps> = ({
    stocks,
    value,
    onChange,
    placeholder = "Rechercher une action...",
    disabled = false
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const wrapperRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLInputElement>(null);

    // Find selected stock object for display
    const selectedStock = stocks.find(s => s.symbole === value);

    // Initialize search term when value or stocks change.
    // Depends on `stocks` (not `selectedStock`) because selectedStock is a derived object
    // whose reference changes every render — depending on it caused the effect to not fire
    // reliably when stocks loaded asynchronously.
    useEffect(() => {
        const found = stocks.find(s => s?.symbole === value);
        if (found) {
            setSearchTerm(`${found.symbole} - ${found.nom_societe ?? ''}`);
        } else if (!value) {
            setSearchTerm('');
        }
    }, [value, stocks]); // eslint-disable-line react-hooks/exhaustive-deps

    // Handle outside clicks
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) {
                setIsOpen(false);
                // Reset search term to selected value if nothing was picked
                const found = stocks.find(s => s?.symbole === value);
                if (found) {
                    setSearchTerm(`${found.symbole} - ${found.nom_societe ?? ''}`);
                } else {
                    setSearchTerm('');
                }
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [value, stocks]);

    // Filter stocks — guard against null symbole/nom_societe (can happen with incomplete DB entries)
    const filteredStocks = stocks.filter(stock => {
        if (!stock.symbole) return false;
        const term = searchTerm.toLowerCase();
        return (
            stock.symbole.toLowerCase().includes(term) ||
            (stock.nom_societe?.toLowerCase() ?? '').includes(term)
        );
    });

    const handleSelect = (stock: Stock) => {
        onChange(stock.symbole);
        setSearchTerm(`${stock.symbole} - ${stock.nom_societe ?? ''}`);
        setIsOpen(false);
    };

    return (
        <div ref={wrapperRef} style={{ position: 'relative', width: '100%', zIndex: isOpen ? 1001 : 1 }}>
            <div
                style={{
                    position: 'relative',
                    display: 'flex',
                    alignItems: 'center'
                }}
            >
                <Search
                    size={18}
                    style={{
                        position: 'absolute',
                        left: '12px',
                        color: '#9ca3af',
                        pointerEvents: 'none'
                    }}
                />

                <input
                    ref={inputRef}
                    type="text"
                    value={searchTerm}
                    onChange={(e) => {
                        setSearchTerm(e.target.value);
                        setIsOpen(true);
                        if (!e.target.value) {
                            onChange(''); // Clear selection if input cleared
                        }
                    }}
                    onFocus={(e) => {
                        setIsOpen(true);
                        e.target.select();
                    }}
                    placeholder={placeholder}
                    disabled={disabled}
                    style={{
                        width: '100%',
                        padding: '12px 12px 12px 40px',
                        fontSize: '15px',
                        border: `1px solid ${isOpen ? '#2563eb' : 'var(--border-color)'}`,
                        borderRadius: '8px',
                        outline: 'none',
                        transition: 'all 0.2s',
                        boxShadow: isOpen ? '0 0 0 3px rgba(37, 99, 235, 0.1)' : 'none',
                        backgroundColor: disabled ? 'var(--bg-box)' : 'var(--bg-input)',
                        color: 'var(--text-main)'
                    }}
                />

                <div style={{ position: 'absolute', right: '12px', display: 'flex', alignItems: 'center' }}>
                    {value && !disabled && (
                        <button
                            type="button"
                            onClick={(e) => {
                                e.stopPropagation();
                                onChange('');
                                setSearchTerm('');
                                inputRef.current?.focus();
                            }}
                            style={{
                                background: 'none',
                                border: 'none',
                                color: '#9ca3af',
                                cursor: 'pointer',
                                padding: 0,
                                marginRight: '8px',
                                display: 'flex'
                            }}
                        >
                            ✕
                        </button>
                    )}
                    {!disabled && <ChevronDown size={16} color="#6b7280" />}
                </div>
            </div>

            {isOpen && !disabled && (
                <div style={{
                    position: 'absolute',
                    top: '100%',
                    left: 0,
                    right: 0,
                    marginTop: '6px',
                    maxHeight: '300px',
                    overflowY: 'auto',
                    background: 'var(--bg-box)',
                    border: '1px solid var(--border-color)',
                    borderRadius: '8px',
                    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.5), 0 2px 4px -1px rgba(0, 0, 0, 0.3)',
                    zIndex: 1000
                }}>
                    {filteredStocks.length > 0 ? (
                        filteredStocks.map((stock) => (
                            <div
                                key={stock.symbole}
                                onClick={() => handleSelect(stock)}
                                style={{
                                    padding: '10px 14px',
                                    cursor: 'pointer',
                                    borderBottom: '1px solid var(--border-color)',
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    background: value === stock.symbole ? 'var(--bg-hover)' : 'transparent',
                                    transition: 'background 0.1s'
                                }}
                                onMouseEnter={(e) => e.currentTarget.style.background = 'var(--bg-hover)'}
                                onMouseLeave={(e) => e.currentTarget.style.background = value === stock.symbole ? 'var(--bg-hover)' : 'transparent'}
                            >
                                <div>
                                    <div style={{ fontWeight: 600, color: 'var(--text-heading)' }}>{stock.symbole}</div>
                                    <div style={{ fontSize: '13px', color: 'var(--text-muted)' }}>{stock.nom_societe}</div>
                                </div>
                                {value === stock.symbole && (
                                    <Check size={16} color="#2563eb" />
                                )}
                            </div>
                        ))
                    ) : (
                        <div style={{ padding: '16px', textAlign: 'center', color: 'var(--text-muted)', fontSize: '14px' }}>
                            Aucune action trouvée
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default StockSearch;

